import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase, type HistorialFila } from "@/lib/supabase";
import { useAlmacen } from "@/lib/almacen";
import { num, signo, fechaHora, etiquetaTipo, etiquetaMotivo } from "@/lib/formato";
import { Boton, Esqueleto, Vacio, Select, Input, Tarjeta, Badge, ErrorBloque } from "@/components/ui";
import { mapearErrorSupabase } from "@/lib/errores";

const POR_PAGINA = 50;

export function Historial() {
  const { almacen, multi } = useAlmacen();
  const [tipo, setTipo] = useState<"todos" | "conteo" | "entrada" | "baja" | "transferencia">("todos");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [pagina, setPagina] = useState(0);

  const q = useQuery({
    queryKey: ["historial", almacen?.id, tipo, desde, hasta, pagina],
    enabled: !!almacen,
    queryFn: async () => {
      let query = supabase.from("v_historial").select("*", { count: "exact" }).eq("almacen_id", almacen!.id);
      if (tipo !== "todos") query = query.eq("tipo", tipo);
      if (desde) query = query.gte("fecha", new Date(desde + "T00:00:00").toISOString());
      if (hasta) query = query.lte("fecha", new Date(hasta + "T23:59:59").toISOString());
      const { data, error, count } = await query.order("fecha", { ascending: false }).range(pagina * POR_PAGINA, pagina * POR_PAGINA + POR_PAGINA - 1);
      if (error) throw error;
      return { filas: data, total: count ?? 0 };
    },
  });

  if (!almacen) return <Esqueleto />;
  const total = q.data?.total ?? 0;
  const paginas = Math.max(1, Math.ceil(total / POR_PAGINA));

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div><h1 className="text-2xl font-bold">Historial</h1><p className="text-sm text-tinta-suave">Conteos y movimientos con quién y cuándo{multi ? ` · ${almacen.nombre}` : ""}.</p></div>
      <div className="flex flex-wrap gap-2">
        {(["todos", "conteo", "entrada", "baja", "transferencia"] as const).map((t) => (
          <button key={t} onClick={() => { setTipo(t); setPagina(0); }} className={`rounded-full px-3 py-1.5 text-sm ${tipo === t ? "bg-sidebar text-white" : "border border-borde bg-white hover:bg-gray-50"}`}>{t === "todos" ? "Todos" : etiquetaTipo(t) + "s"}</button>
        ))}
        <Input type="date" value={desde} onChange={(e) => { setDesde(e.target.value); setPagina(0); }} className="h-9 w-auto text-sm" aria-label="Desde" />
        <Input type="date" value={hasta} onChange={(e) => { setHasta(e.target.value); setPagina(0); }} className="h-9 w-auto text-sm" aria-label="Hasta" />
        <Select value="" onChange={() => undefined} className="hidden" aria-hidden />
      </div>

      {q.isLoading ? <Esqueleto filas={8} /> : q.error ? <ErrorBloque mensaje={mapearErrorSupabase(q.error).mensaje} reintentar={() => q.refetch()} /> : total === 0 ? (
        <Tarjeta><Vacio titulo="Aún no hay movimientos" texto="Cuando alguien cuente, reciba o dé de baja algo, aparece aquí con su nombre." /></Tarjeta>
      ) : (
        <>
          <ul className="divide-y divide-borde rounded-md border border-borde bg-white">
            {q.data!.filas.map((h) => <Fila key={h.id ?? ""} h={h} />)}
          </ul>
          <div className="flex items-center justify-between text-sm text-tinta-suave">
            <span>{total} registro{total === 1 ? "" : "s"}</span>
            {paginas > 1 && <span className="flex items-center gap-2"><Boton variante="secundario" tamano="sm" disabled={pagina === 0} onClick={() => setPagina((p) => p - 1)}>Anterior</Boton>{pagina + 1} / {paginas}<Boton variante="secundario" tamano="sm" disabled={pagina + 1 >= paginas} onClick={() => setPagina((p) => p + 1)}>Siguiente</Boton></span>}
          </div>
        </>
      )}
    </div>
  );
}

function Fila({ h }: { h: HistorialFila }) {
  const delta = Number(h.delta ?? 0);
  const tono = h.tipo === "conteo" ? "info" : h.tipo === "entrada" ? "ok" : h.tipo === "baja" ? "error" : "neutro";
  return (
    <li className="flex items-center gap-3 px-3 py-2.5 text-sm">
      <Badge tono={tono as never} className="w-20 justify-center">{etiquetaTipo(h.tipo)}</Badge>
      <div className="min-w-0 flex-1">
        <div className="truncate"><span className="font-medium">{h.sku}</span> <span className="text-tinta-suave">{h.producto_nombre}</span></div>
        <div className="truncate text-xs text-tinta-suave">
          {h.tipo === "conteo" ? <>{num(h.cantidad_anterior)} → {num(h.cantidad_posterior)}</> : <>{etiquetaMotivo(h.motivo)}{h.referencia ? ` · ${h.referencia}` : ""}{h.nota ? ` · ${h.nota}` : ""}</>}
          {" · "}{h.usuario_nombre} · {fechaHora(h.fecha)}{h.foto_path ? " · 📷" : ""}
        </div>
      </div>
      <div className={`tabular shrink-0 text-right font-semibold ${delta < 0 ? "text-red-700" : delta > 0 ? "text-emerald-700" : "text-tinta-suave"}`}>{signo(delta)}</div>
    </li>
  );
}
